import './Shell.scss';
import { useCallback, useEffect, useMemo, ErrorInfo } from 'react';
import { Routes } from './Routes'
import { useNavigate, useLocation } from 'react-router-dom';
import { useLoadTranslations, useGoogleAnalytics , useEcoRoutes, useDisplayAlert, useRuntimeAuth, useRuntimeEnv } from 'hooks';
import { SnackbarItem, SnackbarList } from 'components/snackbar/SnackbarList';
import ErrorBoundary from './ErrorBoundary';
import ReactGA from 'react-ga';
import { useRedirectToLogin } from 'hooks/runtime/useRedirectToLogin';
import { CookieBanner } from 'components/cookiebanner';
import { CookieBannerState } from 'state/CookieBannerState';
import { useRecoilValue } from 'recoil';

/**
 * 
 * App wide container
 *  
 */
const Shell:React.FC<ShellType> = ({ defaultRoute }: ShellType) => {

    // build react-router routes component from allowed backend routes
    const auth = useRuntimeAuth()
    const env = useRuntimeEnv()
    const getCookieBannerState = useRecoilValue(CookieBannerState)
    const navigate = useNavigate()
    const location = useLocation();
    const translations = useLoadTranslations();
    const routes = useEcoRoutes();
    const displayAlert = useDisplayAlert()
    const redirectToLogin = useRedirectToLogin()

    // track using Google Analytics
    useGoogleAnalytics() ;
    
    useEffect(() => {
        if (routes.length && location.pathname === '/' && defaultRoute) {
            console.log("navigate to default route " + defaultRoute) ;
            navigate(defaultRoute)
        }
    }, [location, routes])

    const serverUnreachableAlert = useMemo<SnackbarItem>(() => {
        return {
            label: 'Server unreachable',
            action: {
                label: 'Refresh',
                onClick: () => {
                    window.location.reload();
                    ga_ServerErrorRefresh();
                }
            }
        }
    }, [])

    const applicationCrashedAlert = useMemo<SnackbarItem>(() => {
        return {
            label: 'Error occured while displaing page.',
            autoHideDuration: 5000,
            action: {
                label: 'Refresh',
                onClick: () => {
                    window.location.reload(); 
                    ga_ErrorRefresh();
                    }
            }
        }
    }, [])

    const applicationAccessDeniedAlert = useMemo<SnackbarItem>(() => {
        return {
            label: `${auth?.user?.displayName} is unauthorized to access.`,
            autoHideDuration: 15000,
            action: {
                label: 'Back to login',
                onClick: () =>  redirectToLogin()
            }
        }
    }, [auth])

    const isServerUnReachable = useMemo(() => { 
        return auth === undefined
    }, [auth])

    const isUnAuthorized = useMemo(() => {
        return env?.reasonCode === 'UNAUTHORIZED_ACCESS_TO_APPLICATION'
    }, [env])

    const isUnAuthenticated = useMemo(() => {
        return auth?.auth.reasonCode === 'USER_NOT_LOGGED_IN'
    }, [auth])

    const redirectTimeout = useCallback(() => {
        displayAlert(applicationAccessDeniedAlert)
        setTimeout(() => redirectToLogin(), 5000)
    }, [])

    const canRedirectToLogin = useMemo(() => {
        return !process.env.REACT_APP_LOGIN && process.env.REACT_APP_LOGIN_URL
    }, [process.env])

    const canAccessApp = useMemo(() => {
        if (process.env.REACT_APP_LOGIN) return true
        return auth !== undefined
    }, [auth])

    const translationsLoaded = useCallback(() => {
        return process.env.REACT_APP_LOGIN || translations.isLoaded() ;
    }, [translations, process.env])

    const handleComponentCatch = (error: Error, errorInfo: ErrorInfo) => {
        console.log(errorInfo)
        displayAlert(applicationCrashedAlert)
        process.env.REACT_APP_ERROR_PAGE && navigate(process.env.REACT_APP_ERROR_PAGE)
    }

    useEffect(() => {
        if (canAccessApp && canRedirectToLogin) {
            if (isUnAuthenticated) {
                redirectToLogin()
            }
            else if (isUnAuthorized) {
                redirectTimeout()
            }
        }

        isServerUnReachable && displayAlert(serverUnreachableAlert)
    }, [isUnAuthenticated, isUnAuthorized, isServerUnReachable, canRedirectToLogin, canAccessApp])

    return (
        <>
            { 
                canAccessApp && translationsLoaded() && 
                <ErrorBoundary onCatch={handleComponentCatch}>
                    <Routes />
                    {process.env.REACT_APP_RGPD === 'true' && getCookieBannerState ? <CookieBanner /> : null }
                </ErrorBoundary>
            }
            <SnackbarList limit={3} />
        </>
    );
}

export type ShellType = { 
    defaultRoute?: string 
}

export default Shell;

const ga_ErrorRefresh = () => {
    ReactGA.event({
      category: 'Event',
      action: 'Refresh',
      label: 'Error/Event/RefreshedAfterError',
    });
  }

const ga_ServerErrorRefresh = () => {
    ReactGA.event({
        category: 'Event',
        action: 'Refresh',
        label: 'Error/Event/RefreshedAfterServerError',
    });
}